import { 
  getStartAndEndOfWeek, 
  weekIdToTimestamp as weekIdToTimestampFromDateUtils,
  capWeekIdToValidWeek,
  getWeekIdRange
} from 'src/utils/dateUtils';
import { SplineChartV2DataPoint, SplineChartV2Series, WeekMeta } from './SplineChartV2';

/**
 * Extended data point with computed metadata (used internally)
 */
export interface SplineChartV2DataPointWithMeta extends SplineChartV2DataPoint {
  meta: WeekMeta;
}

/**
 * Converts a weekId to metadata including timestamps and formatted labels.
 * Uses SDS-based utilities to properly handle week boundaries (e.g., week 53).
 */
export function weekIdToMeta(weekId: number): WeekMeta {
  if (!weekId || weekId < 100000) {
    return { year: 0, week: 0, weekLabel: '0', ts: 0, isoDate: '' };
  }

  // First, cap the weekId to a valid week in case the year doesn't have week 53
  const validWeekId = capWeekIdToValidWeek(weekId);
  
  const year = Math.floor(validWeekId / 100);
  const week = validWeekId % 100;

  // Use SDS-based getStartAndEndOfWeek for proper date handling
  const { startDate, endDate } = getStartAndEndOfWeek(validWeekId, false);
  
  // Use the timestamp from the validated weekId
  const ts = weekIdToTimestampFromDateUtils(validWeekId) ?? 0;

  return {
    year,
    week,
    weekLabel: String(week),
    ts,
    isoDate: endDate ? `${endDate.slice(0, 4)}-${endDate.slice(4, 6)}-${endDate.slice(6, 8)}` : ''
  };
}

/**
 * Formats an ISO date string (YYYY-MM-DD) to "M/D" format (no leading zeros)
 */
export function formatIsoDateToMMDD(isoDate: string): string {
  if (!isoDate || isoDate.length < 10) return '';
  const month = parseInt(isoDate.slice(5, 7), 10);
  const day = parseInt(isoDate.slice(8, 10), 10);
  return `${month}/${day}`;
}

/**
 * Converts a weekId to a timestamp (end of week).
 * Used for formatting x-axis labels.
 * Delegates to the SDS-based implementation in dateUtils.
 */
export function weekIdToTimestamp(weekId: number): number {
  if (!weekId || weekId < 100000) {
    return 0;
  }
  // First cap to valid week, then convert to timestamp
  const validWeekId = capWeekIdToValidWeek(weekId);
  return weekIdToTimestampFromDateUtils(validWeekId) ?? 0;
}

/**
 * Fills category positions sequentially with data points.
 * Data points are mapped 1:1 to category positions by index.
 *
 * @param data - The data points (should be sorted by weekId)
 * @param categoryCount - Number of category positions to fill
 */
export function fillCategoryData(
  data: SplineChartV2DataPoint[],
  categoryCount: number
): (SplineChartV2DataPointWithMeta | null)[] {
  return Array.from({ length: categoryCount }, (_, idx) => {
    const dataPoint = data[idx];
    if (dataPoint) {
      return {
        ...dataPoint,
        meta: weekIdToMeta(dataPoint.weekId)
      };
    }
    return null;
  });
}

/**
 * Utility to create a series from raw data keyed by weekId
 */
export function createSeriesFromWeekIdMap(
  id: string,
  name: string,
  dataByWeekId: Record<number, number | null>,
  options?: Partial<SplineChartV2Series>
): SplineChartV2Series {
  return {
    id,
    name,
    data: Object.entries(dataByWeekId).map(([weekId, value]) => ({
      weekId: Number(weekId),
      value
    })),
    ...options
  };
}

/**
 * Adds metadata to data points
 */
export function addMetaToDataPoints(data: SplineChartV2DataPoint[]): SplineChartV2DataPointWithMeta[] {
  return data.map((point) => ({
    ...point,
    meta: weekIdToMeta(point.weekId)
  }));
}

/**
 * Merges comparison and primary data into one continuous series for prior period mode
 */
export function mergeComparisonAndPrimaryData(
  comparisonData: SplineChartV2DataPoint[],
  primaryData: SplineChartV2DataPoint[]
): SplineChartV2DataPointWithMeta[] {
  const comparisonWithMeta = addMetaToDataPoints(comparisonData);
  const primaryWithMeta = addMetaToDataPoints(primaryData);
  return [...comparisonWithMeta, ...primaryWithMeta];
}

/**
 * Options for aligning data to week IDs
 */
export interface AlignDataToWeekIdsOptions {
  /**
   * When true, replaces missing weeks with 0. When false, uses null.
   * @default true — we pad with 0 (not null) so the line connects through missing weeks.
   */
  fillNullWithZero?: boolean;
}

/**
 * Aligns data points to expected week IDs, filling missing data with 0 (product decision).
 *
 * Missing weeks are padded with 0 so the chart line connects across the full range
 * instead of showing gaps. Pass fillNullWithZero: false to pad with null if needed.
 *
 * @param data - The data points (may have missing weeks)
 * @param expectedWeekIds - The expected week IDs for each position
 * @param options - Optional configuration for alignment behavior
 * @returns An array where each position corresponds to the expected week ID
 *
 * @example
 * // If comparison data is missing week 202502:
 * // data = [{ weekId: 202501, value: 100 }, { weekId: 202503, value: 300 }]
 * // expectedWeekIds = [202501, 202502, 202503]
 * // Returns: [
 * //   { weekId: 202501, value: 100 },
 * //   { weekId: 202502, value: 0 },  // Filled with 0 (default; use fillNullWithZero: false for null)
 * //   { weekId: 202503, value: 300 }
 * // ]
 */
export function alignDataToWeekIds(
  data: SplineChartV2DataPoint[],
  expectedWeekIds: number[],
  options: AlignDataToWeekIdsOptions = {}
): SplineChartV2DataPoint[] {
  // Pad missing data with 0 by default (product decision: smooth line across full range).
  const { fillNullWithZero = true } = options;

  if (!expectedWeekIds.length) return data;

  // Create a lookup map for O(1) access
  const dataByWeekId = new Map(data.map(d => [d.weekId, d]));

  const missingValue = fillNullWithZero ? 0 : null;
  
  return expectedWeekIds.map(weekId => {
    const existing = dataByWeekId.get(weekId);
    if (existing) {
      // If fillNullWithZero is true, also convert existing null values to 0
      if (fillNullWithZero && existing.value === null) {
        return { ...existing, value: 0 };
      }
      return existing;
    }
    // Create an entry for missing week with the appropriate value
    return { weekId, value: missingValue };
  });
}

/**
 * Fills data to match a specified week ID range. Missing weeks are padded with 0
 * (product decision; see alignDataToWeekIds). Use alignDataToWeekIds with
 * fillNullWithZero: false if you need null padding instead.
 *
 * @param data - The data points (may have missing weeks)
 * @param startWeekId - The start week ID of the range
 * @param endWeekId - The end week ID of the range
 * @returns An array with entries for every week in the range
 */
export function fillMissingWeeksWithNull(
  data: SplineChartV2DataPoint[],
  startWeekId: number,
  endWeekId: number
): SplineChartV2DataPoint[] {
  if (!startWeekId || !endWeekId || startWeekId > endWeekId) {
    return data;
  }
  
  const expectedWeekIds = getWeekIdRange(startWeekId, endWeekId);
  return alignDataToWeekIds(data, expectedWeekIds);
}
